// 角色常數定義
export const ROLES = {
  ADMIN: '1',
  MANAGER: '2',
  READER: '3',
  STAFF: 'STAFF' // 空字符串或 null
} as const;

export const ROLE_NAMES = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.READER]: 'Reader',
  [ROLES.STAFF]: 'Staff'
} as const;

export type RoleId = string;
export type RoleName = string;

// 角色權限檢查工具
export const hasRole = (userRoleId: string | undefined, requiredRoleId: string): boolean => {
  // Staff 角色（空字符串或 null）視為 ROLES.STAFF
  const normalizedRoleId = userRoleId || ROLES.STAFF;

  if (!normalizedRoleId) return false;

  // 管理員擁有所有權限
  if (normalizedRoleId === ROLES.ADMIN) return true;

  // 經理擁有經理、讀者和員工權限
  if (normalizedRoleId === ROLES.MANAGER) {
    return [ROLES.MANAGER, ROLES.READER, ROLES.STAFF].includes(requiredRoleId as any);
  }

  // 讀者擁有讀者和員工權限
  if (normalizedRoleId === ROLES.READER) {
    return [ROLES.READER, ROLES.STAFF].includes(requiredRoleId as any);
  }

  // 員工擁有和經理相同的權限
  // if (normalizedRoleId === ROLES.STAFF) {
  //   return [ROLES.MANAGER, ROLES.READER, ROLES.STAFF].includes(requiredRoleId as any);
  // }

  return false;
};

// 檢查是否為管理員
export const isAdmin = (userRoleId: string | undefined): boolean => {
  return userRoleId === ROLES.ADMIN;
};

export const isManagerRole = (userRoleId: string | undefined): boolean => {
    return userRoleId === ROLES.MANAGER;
};

// 檢查是否為管理員或經理
export const isAdminOrManager = (userRoleId: string | undefined): boolean => {
  return userRoleId === ROLES.ADMIN || userRoleId === ROLES.MANAGER;
};

// 檢查是否可以進行核實操作（僅限 Admin 和 Manager，不包括 Staff）
export const canVerify = (userRoleId: string | undefined): boolean => {
  return userRoleId === ROLES.ADMIN || userRoleId === ROLES.MANAGER;
};

// 檢查是否為管理員、經理或讀者
export const isAdminManagerOrReader = (userRoleId: string | undefined): boolean => {
  return [ROLES.ADMIN, ROLES.MANAGER, ROLES.READER].includes(userRoleId || '' as any);
};

// 獲取角色顯示名稱
export const getRoleDisplayName = (roleId: string | undefined): string => {
  if (!roleId) return ROLE_NAMES[ROLES.STAFF];
  return ROLE_NAMES[roleId as keyof typeof ROLE_NAMES] || ROLE_NAMES[ROLES.STAFF];
};

// 角色權限映射
export const ROLE_PERMISSIONS: Record<string, string[]> = {
  [ROLES.ADMIN]: [
    'user.create',
    'user.edit',
    'user.delete',
    'user.view',
    'data.create',
    'data.edit',
    'data.delete',
    'data.view',
    'system.admin'
  ],
  [ROLES.MANAGER]: [
    'user.view',
    'data.create',
    'data.edit',
    'data.delete',
    'data.view'
  ],
  [ROLES.READER]: [
    'data.view'
  ],
  [ROLES.STAFF]: [
    'user.view',
    'data.create',
    'data.edit',
    'data.delete',
    'data.view' // Staff 權限與 Manager 相同
  ]
};

// 檢查特定權限
export const hasPermission = (userRoleId: string | undefined, permission: string): boolean => {
  if (!userRoleId) {
    // Staff 角色（空字符串或 null）使用 Manager 的權限
    const permissions = ROLE_PERMISSIONS[ROLES.MANAGER];
    return permissions ? permissions.includes(permission) : false;
  }

  const permissions = ROLE_PERMISSIONS[userRoleId];
  return permissions ? permissions.includes(permission) : false;
};
